import type { ScopeQuery } from "@app/shared"
import { z } from "zod"
import { http } from "../lib/http"
import { scopeQueryString } from "../scope/scope-query"

/**
 * MUST stay in step with `ActivityEventKind` in
 * `apps/engine/src/activity/dto.ts`.
 *
 * This is a `z.enum`, so an unlisted kind fails the array parse and takes the
 * WHOLE feed down — not just the one row. `onboarding_completed` was already
 * missing here; nothing writes it in production yet, so it was latent rather
 * than live, but it would have broken the dashboard the moment onboarding
 * started recording it.
 */
export const ActivityEventKindSchema = z.enum([
	"document_created",
	"correction_round_opened",
	"merchant_change_flagged",
	"notification_dispatched",
	"onboarding_completed",
	"shipment_status_changed",
	"container_added",
])

export const ActivityEventSchema = z.object({
	id: z.string(),
	kind: ActivityEventKindSchema,
	title: z.string(),
	subtitle: z.string().nullable(),
	createdAt: z.string(),
	link: z.string().nullable(),
})

const ActivityEventListSchema = z.array(ActivityEventSchema)

export type ActivityEvent = z.infer<typeof ActivityEventSchema>
export type ActivityEventKind = z.infer<typeof ActivityEventKindSchema>

/** GET /activity?country=&vertical= -- recent (7 day window, capped at 100) feed. */
export const listRecentActivity = async (
	scope?: ScopeQuery,
	signal?: AbortSignal,
): Promise<ActivityEvent[]> => {
	const qs = scopeQueryString(scope)
	const raw = await http<unknown>(
		`/activity${qs ? `?${qs}` : ""}`,
		signal !== undefined ? { signal } : {},
	)
	return ActivityEventListSchema.parse(raw)
}

export const activityQueryKeys = {
	all: ["activity"] as const,
	recent: (scope?: ScopeQuery) =>
		[
			...activityQueryKeys.all,
			"recent",
			scope?.country ?? "global",
			scope?.vertical ?? "all",
		] as const,
}
